import React, { FC, memo, useState, useEffect, ReactElement } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import i18next from 'i18next';
import { Col, Form, Row } from 'react-bootstrap';
import AppModal from '../app-modal/AppModal';
import SecondaryButton from '../SecondaryButton';

type ContraindicationModalProps = {
  show: boolean;
  onCancel: () => void;
  onAccept: () => void;
};

const ContraindicationModal: FC<ContraindicationModalProps> = ({
  show,
  onCancel,
  onAccept,
}: ContraindicationModalProps): ReactElement => {

  const [contraindicationChecked, setContraindicationChecked] = useState(false);
  const [intendedUserCheckbox, setIntendedUserCheckbox] = useState(false);
  const [canAccept, setCanAccept] = useState(false);

  useEffect(() => {
    setCanAccept(contraindicationChecked && intendedUserCheckbox);
  }, [contraindicationChecked, intendedUserCheckbox]);

  const resetState = (): void => {
    setContraindicationChecked(false);
    setIntendedUserCheckbox(false);
    setCanAccept(false);
  };

  const handleCancel = (): void => {
    resetState();
    onCancel();
  };

  const handleAccept = (): void => {
    if (!canAccept) {
      return;
    }

    resetState();
    onAccept();
  };

  return (
    <AppModal
      size="xl"
      dialogClassName="modal-90w modal-dialog-centered"
      show={show}
      onHide={handleCancel}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {i18next.t('contraindicationModal.title')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="mb-3">
          {i18next.t('contraindicationModal.intro')}
        </p>

        <div
          className="bg-light border rounded-1 p-2"
          style={{ height: '60vh', overflowY: 'auto' }}
          dangerouslySetInnerHTML={{ __html: i18next.t('contraindicationModal.body'),
          }}
        />
      </Modal.Body>

      <Modal.Footer>
        <Row className="w-100">
          <Col md={12} className="mb-2 ps-0">
            <Form.Check
              className='fw-bold'
              inline
              label={i18next.t('contraindicationModal.contraindicationLabel')}
              name="contraindication"
              type='checkbox'
              checked={contraindicationChecked}
              onChange={(event) => setContraindicationChecked(event.target.checked)}
            />
          </Col>

          <Col md={12} className="mb-2 ps-0">
            <Form.Check
              className='fw-bold'
              inline
              label={i18next.t('contraindicationModal.intendedUseLabel')}
              name="intendedUse"
              type='checkbox'
              checked={intendedUserCheckbox}
              onChange={(event) => setIntendedUserCheckbox(event.target.checked)}
            />
          </Col>

          <Col md={6} className="ps-0">
            <SecondaryButton className="float-start" onClick={handleCancel}>
              {i18next.t('generic.cancel')}
            </SecondaryButton>
          </Col>

          <Col md={6}>
            <Button
              type="button"
              className="float-end"
              variant={canAccept ? 'primary' : 'secondary'}
              title={i18next.t('contraindicationModal.acceptMouseOver')}
              disabled={!canAccept}
              onClick={handleAccept}
            >
              {i18next.t('generic.accept')}
            </Button>
          </Col>
        </Row>
      </Modal.Footer>
    </AppModal>
  );
};

export default memo(ContraindicationModal);
